import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FiShoppingBag,
  FiSearch,
  FiMapPin,
  FiTag,
  FiPhone,
  FiHeart,
  FiCalendar,
  FiRefreshCw,
  FiInfo,
  FiLayers,
} from "react-icons/fi";
import {
  getLocalBazaarCategories,
  getLocalBazaarProducts,
  getLocalBazaarDetails,
  toggleLocalBazaarFavorite,
  createMeetingReminder,
} from "../../../api/customer/customerFeatureApis";

export default function CustomerLocalBazaarDirectory() {
  const authState = useSelector((state) => state.auth);
  const token = authState?.user?.token || authState?.token || authState?.user?.refreshToken;

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);

  // Detail Modal State
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [bazaarDetail, setBazaarDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  // Reminder Modal State
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderTitle, setReminderTitle] = useState("");
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [reminderNotes, setReminderNotes] = useState("");
  const [reminderSubmitting, setReminderSubmitting] = useState(false);

  // Fetch Categories
  const fetchCategories = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getLocalBazaarCategories(token, "");
      const cats = data?.localBazaarCategories || [];
      setCategories(cats);
    } catch (err) {
      console.error("Failed to load local bazaar categories:", err);
    }
  }, [token]);

  // Fetch Products
  const fetchProducts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setAlertMsg(null);
    try {
      const data = await getLocalBazaarProducts(token, { page: 1, pageSize: 50 });
      const items = data?.localBazaarList || data?.localBazaars || data?.bazaars || [];
      setProducts(items);
    } catch (err) {
      console.error("Failed to load local bazaar products:", err);
      setAlertMsg({ type: "danger", text: "Failed to load marketplace items. Please refresh." });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, [fetchCategories, fetchProducts]);

  // View Item Details
  const handleOpenDetail = async (itemId) => {
    setShowDetailModal(true);
    setDetailLoading(true);
    try {
      const data = await getLocalBazaarDetails(token, itemId);
      setBazaarDetail(data?.localBazaar || data?.bazaar || null);
    } catch (err) {
      console.error("Failed to fetch bazaar details:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Toggle Favorite
  const handleFavoriteToggle = async () => {
    if (!bazaarDetail?.id) return;
    setFavoriteLoading(true);
    try {
      const isFav = bazaarDetail.is_favorite || bazaarDetail.favorite;
      const favId =
        bazaarDetail.favorite_local_bazaar_id ||
        bazaarDetail.favorite_id ||
        bazaarDetail.local_bazaar_id;
      const res = await toggleLocalBazaarFavorite(token, bazaarDetail.id, isFav, favId);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setBazaarDetail((prev) => ({
          ...prev,
          is_favorite: !isFav,
          favorite_local_bazaar_id: isFav ? null : res.favorite_id || res.id,
        }));
      }
    } catch (err) {
      console.error("Bazaar favorite toggle failed:", err);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Schedule Reminder
  const handleScheduleReminder = async (e) => {
    e.preventDefault();
    if (!reminderDate || !reminderTime || !reminderTitle) {
      alert("Please enter title, date, and time for the reminder.");
      return;
    }
    setReminderSubmitting(true);
    try {
      const payload = {
        title: reminderTitle,
        meeting_date: reminderDate,
        meeting_time: reminderTime,
        note: reminderNotes,
        local_bazaar_id: bazaarDetail?.id || 0,
        property_id: 0,
        interiror_design_id: 0,
        all_services_id: 0,
      };
      const res = await createMeetingReminder(token, payload);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setAlertMsg({ type: "success", text: "Marketplace reminder saved successfully!" });
        setShowReminderModal(false);
        setReminderTitle("");
        setReminderDate("");
        setReminderTime("");
        setReminderNotes("");
      } else {
        alert(res?.text || "Failed to schedule reminder.");
      }
    } catch (err) {
      alert(err?.response?.data?.text || "Failed to schedule reminder.");
    } finally {
      setReminderSubmitting(false);
    }
  };

  // Filtered products
  const filteredProducts = products.filter((item) => {
    if (selectedCategory && item.local_bazaar_category_id !== selectedCategory.id) {
      return false;
    }
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const title = (item.title || "").toLowerCase();
    const desc = (item.description || "").toLowerCase();
    const city = (item.city || "").toLowerCase();
    return title.includes(q) || desc.includes(q) || city.includes(q);
  });

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      {/* Page Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <h3 className="fw-bold mb-1 d-flex align-items-center gap-2 text-dark">
            <FiShoppingBag className="text-primary" /> Local Bazaar Marketplace
          </h3>
          <p className="text-muted mb-0 small">
            Browse verified local marketplace items, building supplies, hardware, and goods from nearby merchants.
          </p>
        </div>
        <button
          className="btn btn-outline-secondary d-flex align-items-center gap-1 btn-sm"
          onClick={() => {
            fetchCategories();
            fetchProducts();
          }}
          disabled={loading}
        >
          <FiRefreshCw className={loading ? "spin-icon" : ""} /> Refresh
        </button>
      </div>

      {alertMsg && (
        <div
          className={`alert alert-${alertMsg.type} alert-dismissible fade show d-flex align-items-center justify-content-between mb-3`}
          role="alert"
        >
          <span>{alertMsg.text}</span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setAlertMsg(null)}
          />
        </div>
      )}

      {/* Categories & Search Filter Bar */}
      <div className="card border-0 shadow-sm rounded-3 mb-4">
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-8">
              <div className="d-flex gap-2 overflow-auto pb-1 align-items-center">
                <button
                  className={`btn btn-sm rounded-pill px-3 py-1 text-nowrap ${
                    !selectedCategory ? "btn-primary shadow-sm" : "btn-outline-light text-dark border bg-white"
                  }`}
                  onClick={() => setSelectedCategory(null)}
                >
                  All Items
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`btn btn-sm rounded-pill px-3 py-1 text-nowrap ${
                      selectedCategory?.id === cat.id
                        ? "btn-primary shadow-sm"
                        : "btn-outline-light text-dark border bg-white"
                    }`}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0">
                  <FiSearch size={14} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search products by title, city..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchQuery("")}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading marketplace...</span>
          </div>
          <p className="text-muted mt-2 small">Loading Local Bazaar products...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-3 py-5 text-center">
          <div className="card-body">
            <FiShoppingBag size={48} className="text-muted mb-3 opacity-50" />
            <h5 className="fw-semibold text-secondary">No Products Available</h5>
            <p className="text-muted small mb-0">
              {searchQuery
                ? `No items match "${searchQuery}".`
                : "No items listed under this category at this moment."}
            </p>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {filteredProducts.map((item) => {
            const photos = item.photos || item.bazaar_photos || [];
            const thumbnail = photos[0]?.url
              ? `https://images.allhomedesire.com${photos[0].url}`
              : null;
            const price = item.price ? Number(item.price).toLocaleString("en-IN") : null;

            return (
              <div key={item.id} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                <div className="card h-100 border-0 shadow-sm rounded-3 overflow-hidden d-flex flex-column">
                  {thumbnail ? (
                    <img
                      src={thumbnail}
                      alt={item.title || "Product"}
                      className="w-100 object-fit-cover"
                      style={{ height: "170px" }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div
                      className="w-100 bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center text-muted"
                      style={{ height: "170px" }}
                    >
                      <FiLayers size={36} />
                    </div>
                  )}

                  <div className="card-body p-3 d-flex flex-column justify-content-between flex-grow-1">
                    <div>
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <span className="badge bg-light text-secondary border small">
                          {item.localBazaarCategory?.name || "Bazaar Item"}
                        </span>
                        {item.city && (
                          <span className="text-muted small d-flex align-items-center gap-1">
                            <FiMapPin size={11} className="text-danger" /> {item.city}
                          </span>
                        )}
                      </div>

                      <h6 className="fw-bold text-dark text-truncate mb-1" title={item.title}>
                        {item.title || "Marketplace Product"}
                      </h6>

                      <p
                        className="text-muted small mb-2 line-clamp-2"
                        style={{ minHeight: "36px" }}
                      >
                        {item.description || "Local merchant item available for pickup or delivery."}
                      </p>

                      {price && (
                        <div className="fw-bold text-primary mb-2 fs-5">
                          ₹{price}{" "}
                          <span className="small text-muted fw-normal fs-6">
                            {item.unit ? `/${item.unit}` : ""}
                          </span>
                        </div>
                      )}
                    </div>

                    <button
                      className="btn btn-outline-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-1 mt-2"
                      onClick={() => handleOpenDetail(item.id)}
                    >
                      <FiInfo size={14} /> View Details & Contact
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* =========================================================================
          PRODUCT DETAIL MODAL
          ========================================================================= */}
      {showDetailModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content border-0 shadow rounded-3">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-dark">
                  {bazaarDetail?.title || "Product Details"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailModal(false)}
                />
              </div>

              <div className="modal-body p-4">
                {detailLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary spinner-border-sm" role="status" />
                    <p className="text-muted small mt-2">Loading product details...</p>
                  </div>
                ) : bazaarDetail ? (
                  <div>
                    {/* Media Gallery */}
                    {bazaarDetail.photos?.length > 0 && (
                      <div className="d-flex gap-2 overflow-auto pb-2 mb-3">
                        {bazaarDetail.photos.map((p, idx) => (
                          <img
                            key={idx}
                            src={`https://images.allhomedesire.com${p.url}`}
                            alt="Product Photo"
                            className="rounded-3 object-fit-cover shadow-sm"
                            style={{ width: "220px", height: "150px" }}
                          />
                        ))}
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        {bazaarDetail.price && (
                          <h4 className="fw-bold text-primary mb-0">
                            ₹{Number(bazaarDetail.price).toLocaleString("en-IN")}{" "}
                            <span className="small text-muted fw-normal">
                              {bazaarDetail.unit ? `/${bazaarDetail.unit}` : ""}
                            </span>
                          </h4>
                        )}
                        <span className="badge bg-light text-secondary border mt-1">
                          {bazaarDetail.localBazaarCategory?.name || "Local Bazaar"}
                        </span>
                      </div>

                      <button
                        className={`btn btn-sm d-flex align-items-center gap-1 ${
                          bazaarDetail.is_favorite || bazaarDetail.favorite
                            ? "btn-danger"
                            : "btn-outline-danger"
                        }`}
                        onClick={handleFavoriteToggle}
                        disabled={favoriteLoading}
                      >
                        <FiHeart size={14} />
                        {bazaarDetail.is_favorite || bazaarDetail.favorite ? "Favorited" : "Favorite"}
                      </button>
                    </div>

                    <h6 className="fw-bold text-dark mb-1">Description</h6>
                    <p className="text-secondary small mb-4" style={{ whiteSpace: "pre-wrap" }}>
                      {bazaarDetail.description || "No description provided."}
                    </p>

                    {/* Merchant Info Card */}
                    <div className="card bg-light border-0 p-3 rounded-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-bold text-dark">
                            {bazaarDetail.partner?.first_name} {bazaarDetail.partner?.last_name || "Merchant"}
                          </div>
                          <div className="text-muted small">
                            <FiMapPin size={12} className="me-1 text-danger" />
                            {bazaarDetail.city || bazaarDetail.partner?.city || "Local Market"}
                          </div>
                        </div>

                        <div className="d-flex gap-2">
                          {bazaarDetail.partner?.mobile_number && (
                            <a
                              href={`tel:${bazaarDetail.partner.mobile_number}`}
                              className="btn btn-outline-success btn-sm d-flex align-items-center gap-1"
                            >
                              <FiPhone size={13} /> Call Merchant
                            </a>
                          )}
                          <button
                            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                            onClick={() => {
                              setReminderTitle(`Inquiry for ${bazaarDetail.title}`);
                              setShowReminderModal(true);
                            }}
                          >
                            <FiCalendar size={13} /> Add Reminder
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-muted text-center py-4">Product information unavailable.</div>
                )}
              </div>

              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowDetailModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          SCHEDULE REMINDER MODAL
          ========================================================================= */}
      {showReminderModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow rounded-3">
              <form onSubmit={handleScheduleReminder}>
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                    <FiCalendar className="text-primary" /> Schedule Product Reminder
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowReminderModal(false)}
                    disabled={reminderSubmitting}
                  />
                </div>
                <div className="modal-body py-3">
                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-dark">Reminder Title *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={reminderTitle}
                      onChange={(e) => setReminderTitle(e.target.value)}
                      required
                    />
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <label className="form-label fw-semibold small text-dark">Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={reminderDate}
                        onChange={(e) => setReminderDate(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-semibold small text-dark">Time *</label>
                      <input
                        type="time"
                        className="form-control"
                        value={reminderTime}
                        onChange={(e) => setReminderTime(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-dark">Notes</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Add inquiry details or pickup notes..."
                      value={reminderNotes}
                      onChange={(e) => setReminderNotes(e.target.value)}
                    />
                  </div>
                </div>

                <div className="modal-footer border-0 pt-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setShowReminderModal(false)}
                    disabled={reminderSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm px-4"
                    disabled={reminderSubmitting}
                  >
                    {reminderSubmitting ? "Saving..." : "Save Reminder"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
